import { z } from 'zod'
import { productSummarySchema } from './catalog.ts'
import { currencySchema, minorAmountSchema } from './money.ts'

/**
 * The locked deal on the buy side, and how an order gets covered.
 *
 * A price check (`sourcing.ts`) asks shops what they would charge. This is what
 * happens after the desk picks one: a written order to that shop, which it accepts
 * or turns down, and a worksheet saying which shop covers which part of whose
 * order.
 *
 * The anonymity boundary is the same as everywhere else and is carried by the
 * types, not by a filter. `PurchaseOrder` names the shop and may name the customer
 * order behind it; `SupplyOrder` — what the shop is shown — has no field for either
 * the customer or what the platform will sell the goods for (D4).
 */

export const PURCHASE_ORDER_STATUSES = [
  'sent',
  'confirmed',
  'declined',
  'received',
  'cancelled',
] as const
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number]
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES)

/** Plain language, both sides. A shop never reads the words "purchase order". */
export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  sent: 'Waiting on the shop',
  confirmed: 'Accepted',
  declined: 'Turned down',
  received: 'Goods in',
  cancelled: 'Cancelled',
}

/** The same rows from the shop's side of the counter. */
export const SUPPLY_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  sent: 'Needs your answer',
  confirmed: 'You accepted',
  declined: 'You passed',
  received: 'Delivered',
  cancelled: 'Cancelled by the desk',
}

export const PURCHASE_ORDER_TRANSITIONS: Record<
  PurchaseOrderStatus,
  readonly PurchaseOrderStatus[]
> = {
  sent: ['confirmed', 'declined', 'cancelled'],
  confirmed: ['received', 'cancelled'],
  declined: [],
  received: [],
  cancelled: [],
}

export function canTransitionPurchaseOrder(
  from: PurchaseOrderStatus,
  to: PurchaseOrderStatus,
): boolean {
  return PURCHASE_ORDER_TRANSITIONS[from].includes(to)
}

export const purchaseOrderLineSchema = z.object({
  id: z.string(),
  product: productSummarySchema,
  qty: z.number().int(),
  unitCostMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
})
export type PurchaseOrderLine = z.infer<typeof purchaseOrderLineSchema>

export const purchaseOrderSchema = z.object({
  id: z.string(),
  code: z.string(),
  orgId: z.string(),
  orgName: z.string(),
  status: purchaseOrderStatusSchema,
  currency: currencySchema,
  subtotalMinor: z.number().int(),
  totalMinor: z.number().int(),
  deliveryCity: z.string().nullable(),
  note: z.string().nullable(),
  /** The customer order this covers, if it is not a stock buy. Desk-only. */
  orderId: z.string().nullable(),
  orderCode: z.string().nullable(),
  sourcingRequestId: z.string().nullable(),
  expectedAt: z.number().int().nullable(),
  confirmedAt: z.number().int().nullable(),
  receivedAt: z.number().int().nullable(),
  lines: z.array(purchaseOrderLineSchema),
  version: z.number().int(),
  createdAt: z.number().int(),
})
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>

/**
 * A shop's view. There is no `orderId`, no customer, and no sell price — not
 * filtered out, simply absent from the type, so a mapper cannot leak one by
 * forgetting a check.
 */
export const supplyOrderSchema = z.object({
  id: z.string(),
  code: z.string(),
  status: purchaseOrderStatusSchema,
  currency: currencySchema,
  subtotalMinor: z.number().int(),
  totalMinor: z.number().int(),
  deliveryCity: z.string().nullable(),
  note: z.string().nullable(),
  expectedAt: z.number().int().nullable(),
  lines: z.array(purchaseOrderLineSchema),
  createdAt: z.number().int(),
})
export type SupplyOrder = z.infer<typeof supplyOrderSchema>

export const createPurchaseOrderSchema = z.object({
  orgId: z.string().min(1, 'Pick a shop.'),
  lines: z
    .array(
      z.object({
        productId: z.string().min(1),
        qty: z.number().int().min(1, 'Enter a quantity.'),
        unitCostMinor: minorAmountSchema,
      }),
    )
    .min(1, 'Add at least one item.')
    .max(100),
  /**
   * When set, the order this covers. Every line that matches one of its lines is
   * allocated straight away — placing the order and recording who covers it are
   * one decision, and splitting them is how a worksheet goes stale.
   */
  orderId: z.string().max(64).nullish(),
  sourcingRequestId: z.string().max(64).nullish(),
  deliveryCity: z.string().trim().max(80).nullish(),
  note: z.string().trim().max(500).nullish(),
  expectedInDays: z.number().int().min(0).max(365).nullish(),
})
export type CreatePurchaseOrderInput = z.infer<typeof createPurchaseOrderSchema>

export const advancePurchaseOrderSchema = z.object({
  status: purchaseOrderStatusSchema,
  note: z.string().trim().max(500).nullish(),
})
export type AdvancePurchaseOrderInput = z.infer<typeof advancePurchaseOrderSchema>

// ---------------------------------------------------------------------------
// Allocations — the admin's sourcing worksheet
// ---------------------------------------------------------------------------

export const ALLOCATION_SOURCES = ['platform_stock', 'purchase_order'] as const
export type AllocationSource = (typeof ALLOCATION_SOURCES)[number]

export const ALLOCATION_SOURCE_LABELS: Record<AllocationSource, string> = {
  platform_stock: 'Our own stock',
  purchase_order: 'A shop',
}

export const orderAllocationSchema = z.object({
  id: z.string(),
  orderLineId: z.string(),
  productId: z.string(),
  source: z.enum(ALLOCATION_SOURCES),
  purchaseOrderId: z.string().nullable(),
  purchaseOrderCode: z.string().nullable(),
  orgName: z.string().nullable(),
  qty: z.number().int(),
  createdAt: z.number().int(),
})
export type OrderAllocation = z.infer<typeof orderAllocationSchema>

export const createAllocationSchema = z
  .object({
    orderLineId: z.string().min(1),
    source: z.enum(ALLOCATION_SOURCES),
    purchaseOrderId: z.string().max(64).nullish(),
    qty: z.number().int().min(1, 'Enter a quantity.'),
  })
  .refine((v) => (v.source === 'purchase_order') === Boolean(v.purchaseOrderId), {
    message: 'Pick the shop order this comes from.',
    path: ['purchaseOrderId'],
  })
export type CreateAllocationInput = z.infer<typeof createAllocationSchema>
